import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, CheckCircle2, RefreshCw, ShieldCheck, FileText, PenLine, FileSignature, BookOpen, Globe } from "lucide-react";
import SignaturePad from "@/components/SignaturePad";
import { api, formatApiErrorDetail } from "@/lib/api";
import { docTextStyle, DEFAULT_DISPLAY } from "@/lib/displayStyle";

const T = {
  de: {
    title: "Digitale Einwilligung",
    yourData: "Ihre Daten", dataIntro: "Alle Felder sind Pflicht.",
    firstname: "Vorname", lastname: "Nachname", email: "E-Mail", dob: "Geburtsdatum",
    street: "Straße & Hausnummer", zip: "PLZ", city: "Ort",
    emailInvalid: "Bitte eine gültige E-Mail-Adresse eingeben (z. B. name@praxis.de).",
    zipInvalid: "Die PLZ muss aus genau 5 Ziffern bestehen.",
    nextToPrivacy: "Weiter zur Datenschutzerklärung", nextToPrivacyShort: "Weiter",
    privacy: "Datenschutzerklärung", privacyShort: "Datenschutz",
    contract: "Behandlungsvertrag", contractShort: "Vertrag",
    readSub: "Bitte in Ruhe lesen und am Ende bestätigen.", version: "Version",
    confirmRead: "Ich habe das Dokument gelesen und stimme zu. *",
    yes: "Ja", no: "Nein", questionsTitle: "Bitte beantworten Sie folgende Fragen:",
    questionsHint: "Bitte beantworten Sie alle Fragen mit Ja oder Nein.",
    nextToSign: "Weiter zur Unterschrift", nextToSignShort: "Unterschreiben",
    signHeading: (d) => `${d} unterschreiben`,
    signSub: "Bitte unterschreiben Sie mit Finger oder Stift.",
    reset: "Zurücksetzen", back: "Zurück",
    nextToContract: "Weiter zum Behandlungsvertrag", nextToContractShort: "Weiter",
    submit: "Absenden", submitting: "Wird gesendet…",
    thanksOne: "Vielen Dank für Ihre Unterschrift!",
    thanksTwo: "Vielen Dank für Ihre Unterschriften!",
    storedOne: "Ihre Datenschutzerklärung wurde sicher gespeichert.",
    storedTwo: "Datenschutzerklärung und Behandlungsvertrag wurden sicher gespeichert.",
    closing: (n) => `Sitzung wird in ${n} Sekunde${n === 1 ? "" : "n"} beendet…`,
    sessionEnded: "Sitzung beendet",
    sessionEndedSub: "Bitte das Gerät an die Praxis zurückgeben. Für eine neue Einwilligung muss der QR-Code erneut gescannt werden.",
    step: "Schritt", of: "von",
    steps: { data: "Daten", privacyRead: "DS lesen", privacySign: "DS unterschreiben",
             contractRead: "Vertrag lesen", contractSign: "Vertrag unterschreiben", done: "Abschluss" },
    translating: "Englische Fassung wird vorbereitet…",
    translateNote: "Translation for convenience — only the German version is legally binding.",
  },
  en: {
    title: "Digital Consent",
    yourData: "Your details", dataIntro: "All fields are required.",
    firstname: "First name", lastname: "Last name", email: "Email", dob: "Date of birth",
    street: "Street & number", zip: "ZIP", city: "City",
    emailInvalid: "Please enter a valid email address (e.g. name@clinic.com).",
    zipInvalid: "The ZIP code must be exactly 5 digits.",
    nextToPrivacy: "Continue to privacy policy", nextToPrivacyShort: "Continue",
    privacy: "Privacy Policy", privacyShort: "Privacy",
    contract: "Treatment Contract", contractShort: "Contract",
    readSub: "Please read carefully and confirm.", version: "Version",
    confirmRead: "I have read the document and agree. *",
    yes: "Yes", no: "No", questionsTitle: "Please answer the following questions:",
    questionsHint: "Please answer all questions with Yes or No.",
    nextToSign: "Continue to signature", nextToSignShort: "Sign",
    signHeading: (d) => `Sign the ${d}`,
    signSub: "Please sign with your finger or a pen.",
    reset: "Reset", back: "Back",
    nextToContract: "Continue to treatment contract", nextToContractShort: "Continue",
    submit: "Submit", submitting: "Sending…",
    thanksOne: "Thank you for your signature!",
    thanksTwo: "Thank you for your signatures!",
    storedOne: "Your privacy consent has been securely stored.",
    storedTwo: "Privacy policy and treatment contract have been securely stored.",
    closing: (n) => `Session ending in ${n} second${n === 1 ? "" : "s"}…`,
    sessionEnded: "Session ended",
    sessionEndedSub: "Please hand the device back. To start a new consent, scan the QR code again.",
    step: "Step", of: "of",
    steps: { data: "Details", privacyRead: "Read", privacySign: "Sign",
             contractRead: "Read contract", contractSign: "Sign contract", done: "Done" },
    translating: "Preparing English version…",
    translateNote: "Translation for convenience — only the German version is legally binding.",
  },
};

// Kompakter Progress-Indikator: dots + "Schritt X / Y" Text — mobile-first
function Progress({ steps, step, t }) {
  return (
    <div className="mb-6 sm:mb-10">
      <div className="flex items-center justify-between mb-2 px-1">
        <p className="text-xs sm:text-sm font-medium text-emerald-900 uppercase tracking-wider">
          {steps[step]?.label}
        </p>
        <p className="text-xs sm:text-sm text-stone-500 tabular-nums">
          {t.step} {step + 1} {t.of} {steps.length}
        </p>
      </div>
      <div className="flex gap-1.5">
        {steps.map((s, i) => (
          <div key={s.key} className={`h-1.5 flex-1 rounded-full transition-colors ${
            i < step ? "bg-emerald-700" : i === step ? "bg-emerald-900" : "bg-stone-200"
          }`} />
        ))}
      </div>
    </div>
  );
}

// Bottom-Action-Bar mobil: gestackt full-width; sm+: nebeneinander
function Actions({ onBack, onNext, nextLabel, nextShort, disabled, submitting, primaryTest, backTest, t }) {
  return (
    <div className="mt-8 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
      <Button data-testid={backTest} variant="secondary" onClick={onBack}
        className="h-14 sm:h-16 px-5 text-base sm:text-lg rounded-xl bg-stone-100 hover:bg-stone-200 text-stone-900 w-full sm:w-auto">
        <ArrowLeft className="mr-2 w-5 h-5 shrink-0" /> {t.back}
      </Button>
      <Button data-testid={primaryTest} disabled={disabled} onClick={onNext}
        className="h-14 sm:h-16 px-5 sm:px-8 text-base sm:text-lg rounded-xl bg-emerald-900 hover:bg-emerald-800 text-white disabled:opacity-40 w-full sm:w-auto">
        <span className="sm:hidden truncate">{submitting ? t.submitting : nextShort}</span>
        <span className="hidden sm:inline truncate">{submitting ? t.submitting : nextLabel}</span>
        <ArrowRight className="ml-2 w-5 h-5 shrink-0" />
      </Button>
    </div>
  );
}

function ReadStep({ testid, heading, version, text, loading, consent, setConsent, onBack, onNext, nextLabel, nextShort, t, lang, questions, answers, setAnswer, displaySettings }) {
  const qs = questions || [];
  const allAnswered = qs.every((q) => answers && answers[q.id] !== undefined);
  const ds = docTextStyle(displaySettings);
  return (
    <div data-testid={testid}>
      <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold text-emerald-900 mb-2 break-words">{heading}</h1>
      <p className="text-stone-600 mb-4 sm:mb-6 text-base sm:text-lg">{t.readSub} {t.version} {version || "1.0"}.</p>
      {lang === "en" && (<div className="mb-3 text-xs text-stone-500 italic">{t.translateNote}</div>)}
      <ScrollArea data-testid={`${testid}-scroll`} className="h-[50vh] sm:h-[55vh] min-h-[320px] rounded-2xl border border-stone-200 bg-stone-50 p-4 sm:p-6 md:p-8">
        <div lang={ds.lang} style={ds.style}
          className={`whitespace-pre-wrap break-words text-stone-800 leading-relaxed ${ds.sizeClass}`}>
          {loading ? t.translating : (text || "…")}
        </div>
      </ScrollArea>

      {qs.length > 0 && (
        <div data-testid={`${testid}-questions`} className="mt-4 sm:mt-5 space-y-3">
          <p className="text-sm sm:text-base font-medium text-stone-800">{t.questionsTitle}</p>
          {qs.map((q) => (
            <div key={q.id} data-testid={`${testid}-question-${q.id}`}
              className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-2xl bg-stone-50 border border-stone-200">
              <span className="text-sm sm:text-base text-stone-800 leading-snug">{q.text}</span>
              <div className="flex gap-2 shrink-0">
                <button type="button" data-testid={`${testid}-q-${q.id}-yes`} onClick={() => setAnswer(q.id, true)}
                  className={`h-11 px-5 rounded-xl text-base font-medium border transition-colors ${answers[q.id] === true ? "bg-emerald-900 text-white border-emerald-900" : "bg-white text-stone-700 border-stone-300 hover:border-emerald-400"}`}>
                  {t.yes}
                </button>
                <button type="button" data-testid={`${testid}-q-${q.id}-no`} onClick={() => setAnswer(q.id, false)}
                  className={`h-11 px-5 rounded-xl text-base font-medium border transition-colors ${answers[q.id] === false ? "bg-stone-700 text-white border-stone-700" : "bg-white text-stone-700 border-stone-300 hover:border-stone-400"}`}>
                  {t.no}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <label htmlFor={`consent-${testid}`} className="mt-4 sm:mt-5 flex items-start gap-3 sm:gap-4 p-4 sm:p-5 rounded-2xl bg-emerald-50 border border-emerald-100 cursor-pointer">
        <Checkbox id={`consent-${testid}`} data-testid={`${testid}-checkbox`} checked={consent}
          onCheckedChange={(v) => setConsent(Boolean(v))} disabled={loading}
          className="w-6 h-6 sm:w-7 sm:h-7 mt-0.5 border-emerald-900 data-[state=checked]:bg-emerald-900 shrink-0" />
        <span className="text-sm sm:text-base leading-relaxed text-stone-800">{t.confirmRead}</span>
      </label>
      {qs.length > 0 && !allAnswered && (
        <p data-testid={`${testid}-questions-hint`} className="text-xs text-orange-600 mt-2">{t.questionsHint}</p>
      )}
      <Actions onBack={onBack} onNext={onNext} nextLabel={nextLabel} nextShort={nextShort}
        disabled={!consent || loading || !allAnswered} primaryTest={`${testid}-next`} backTest={`${testid}-back`} t={t} />
    </div>
  );
}

function SignStep({ testid, heading, sigRef, setSigEmpty, sigEmpty, onBack, onNext, nextLabel, nextShort, submitting, t }) {
  return (
    <div data-testid={testid}>
      <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold text-emerald-900 mb-2 break-words">{heading}</h1>
      <p className="text-stone-600 mb-4 sm:mb-6 text-base sm:text-lg">{t.signSub}</p>
      <SignaturePad ref={sigRef} onChange={(empty) => setSigEmpty(empty)} />
      <div className="mt-2 sm:mt-3 flex justify-end">
        <Button data-testid={`${testid}-clear`} variant="ghost"
          onClick={() => { sigRef.current?.clear(); setSigEmpty(true); }}
          className="text-orange-600 hover:bg-orange-50 h-11 rounded-xl text-sm sm:text-base">
          <RefreshCw className="mr-2 w-4 h-4" /> {t.reset}
        </Button>
      </div>
      <Actions onBack={onBack} onNext={onNext} nextLabel={nextLabel} nextShort={nextShort}
        disabled={sigEmpty || submitting} submitting={submitting}
        primaryTest={`${testid}-next`} backTest={`${testid}-back`} t={t} />
    </div>
  );
}

export default function PatientKiosk() {
  const navigate = useNavigate();
  const [lang, setLang] = useState("de");
  const t = T[lang];
  const [step, setStep] = useState(0);
  const [practice, setPractice] = useState(null);
  const [policy, setPolicy] = useState({ version: "1.0", text: "", questions: [] });
  const [contract, setContract] = useState({ version: null, text: null });
  const [loadingDoc, setLoadingDoc] = useState(false);

  const [privacyAnswers, setPrivacyAnswers] = useState({});
  const setAnswer = (qid, val) => setPrivacyAnswers((a) => ({ ...a, [qid]: val }));

  const [form, setForm] = useState({ firstname: "", lastname: "", email: "", birthdate: "", street: "", zip: "", city: "" });
  const [touched, setTouched] = useState({});
  const markTouched = (k) => setTouched((tt) => ({ ...tt, [k]: true }));
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
  const zipValid = /^\d{5}$/.test(form.zip.trim());

  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [privacySigEmpty, setPrivacySigEmpty] = useState(true);
  const privacySigRef = useRef(null);
  const privacySigDataRef = useRef(null);

  const [contractConsent, setContractConsent] = useState(false);
  const [contractSigEmpty, setContractSigEmpty] = useState(true);
  const contractSigRef = useRef(null);

  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [sessionEnded, setSessionEnded] = useState(false);
  const [endedLogoUrl, setEndedLogoUrl] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [countdown, setCountdown] = useState(5);

  const hasContract = Boolean(contract.text);

  useEffect(() => {
    api.get("/patient/practice").then(({ data }) => setPractice(data)).catch(() => {
      toast.error("Kein Patientenmodus aktiv. / No active session.");
      navigate("/login", { replace: true });
    });
  }, [navigate]);

  // Logo authentifiziert als Blob laden (ein <img src>-Tag sendet keinen Kiosk-Token → 401).
  useEffect(() => {
    if (!practice?.has_logo) { setLogoUrl(null); return; }
    let url = null;
    api.get("/patient/logo", { responseType: "blob" })
      .then((resp) => { url = URL.createObjectURL(resp.data); setLogoUrl(url); })
      .catch(() => setLogoUrl(null));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [practice]);

  useEffect(() => {
    if (!practice) return;
    setLoadingDoc(true);
    Promise.all([
      api.get(`/patient/document/privacy?lang=${lang}`),
      api.get(`/patient/document/treatment?lang=${lang}`),
    ]).then(([a, b]) => {
      setPolicy({ version: a.data.version || "1.0", text: a.data.text || "", questions: Array.isArray(a.data.questions) ? a.data.questions : [] });
      setContract({ version: b.data.version, text: b.data.text });
    }).catch(() => {
      if (lang === "en") { toast.error("English translation not available — falling back to German."); setLang("de"); }
    }).finally(() => setLoadingDoc(false));
  }, [practice, lang]);

  // Nach Erfolg: 5 s Countdown → Logo als data-URL cachen → Kiosk-Token serverseitig löschen → Final-Screen
  useEffect(() => {
    if (!done) return;
    setCountdown(5);
    const tick = setInterval(() => setCountdown((c) => Math.max(0, c - 1)), 1000);
    const tm = setTimeout(async () => {
      // Bereits geladenes Logo-Blob (bleibt nach Logout gültig) für den Final-Screen wiederverwenden.
      if (logoUrl) setEndedLogoUrl(logoUrl);
      try { await api.post("/auth/logout"); } catch (_e) { /* ignore */ }
      try { localStorage.removeItem("auth_token"); } catch (_e) { /* ignore */ }
      setSessionEnded(true);
    }, 5000);
    return () => { clearInterval(tick); clearTimeout(tm); };
  }, [done, logoUrl]);

  const STEPS = useMemo(() => {
    const arr = [
      { key: "data", label: t.steps.data, icon: FileText },
      { key: "privacy-read", label: t.steps.privacyRead, icon: BookOpen },
      { key: "privacy-sign", label: t.steps.privacySign, icon: PenLine },
    ];
    if (hasContract) {
      arr.push({ key: "contract-read", label: t.steps.contractRead, icon: BookOpen });
      arr.push({ key: "contract-sign", label: t.steps.contractSign, icon: FileSignature });
    }
    arr.push({ key: "done", label: t.steps.done, icon: CheckCircle2 });
    return arr;
  }, [hasContract, t]);

  const canNextData = ["firstname", "lastname", "email", "birthdate", "street", "zip", "city"]
    .every((k) => (form[k] || "").trim().length > 0) && emailValid && zipValid;
  const stepKey = STEPS[step]?.key;

  const afterPrivacySign = () => {
    try { privacySigDataRef.current = privacySigRef.current?.toDataURL() || null; } catch (_e) { /* ignore */ }
    if (!privacySigDataRef.current) { toast.error("Bitte Unterschrift leisten."); return; }
    if (hasContract) setStep(3);
    else submitAll();
  };

  const submitAll = async () => {
    const privacySig = privacySigDataRef.current || privacySigRef.current?.toDataURL();
    if (!privacySig) { toast.error("Bitte Datenschutz-Unterschrift erneut leisten."); setStep(2); return; }
    setSubmitting(true);
    try {
      const payload = {
        patient_firstname: form.firstname.trim(),
        patient_lastname: form.lastname.trim(),
        patient_email: form.email.trim(),
        patient_birthdate: form.birthdate,
        patient_street: form.street.trim(),
        patient_zip: form.zip.trim(),
        patient_city: form.city.trim(),
        consent_given: privacyConsent,
        signature_b64: privacySig,
        privacy_answers: privacyAnswers,
      };
      if (hasContract) {
        const csig = contractSigRef.current?.toDataURL();
        if (!csig) { toast.error("Bitte Behandlungsvertrag unterschreiben."); setSubmitting(false); return; }
        payload.treatment_consent_given = contractConsent;
        payload.treatment_signature_b64 = csig;
      }
      await api.post("/patient/consent/submit", payload);
      setDone(true); setStep(STEPS.length - 1);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Absenden fehlgeschlagen.");
    } finally { setSubmitting(false); }
  };

  if (!practice) return <div className="min-h-screen grid place-items-center text-stone-400">Lädt…</div>;

  // FINAL screen: Sitzung beendet — KEIN Redirect zu /login
  if (sessionEnded) {
    return (
      <div className="min-h-[100dvh] bg-emerald-950 grid place-items-center px-6 py-10">
        <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-8 sm:p-10 text-center">
          {endedLogoUrl ? (
            <img src={endedLogoUrl} alt={practice.name}
              className="w-20 h-20 sm:w-24 sm:h-24 mx-auto rounded-2xl object-contain bg-stone-50 border border-stone-200 mb-5" />
          ) : (
            <div className="w-16 h-16 mx-auto rounded-2xl bg-emerald-900 text-white grid place-items-center mb-5">
              <CheckCircle2 className="w-8 h-8" />
            </div>
          )}
          <h1 data-testid="session-ended" className="font-display text-2xl sm:text-3xl font-semibold text-emerald-900 mb-3">
            {t.sessionEnded}
          </h1>
          <p className="text-stone-600 leading-relaxed">{t.sessionEndedSub}</p>
        </div>
      </div>
    );
  }

  const reqField = (key, label, type = "text", extra = {}, error = null) => {
    const showErr = touched[key] && error;
    return (
      <div>
        <Label className="text-sm sm:text-base font-medium text-stone-800">{label} *</Label>
        <Input data-testid={`patient-${key}-input`} type={type} value={form[key]}
          onChange={(e) => setForm({ ...form, [key]: e.target.value })}
          onBlur={() => markTouched(key)}
          aria-invalid={showErr ? true : undefined}
          className={`h-12 sm:h-14 px-4 text-base sm:text-lg rounded-xl mt-2 ${showErr ? "border-red-400 focus-visible:ring-red-300" : ""}`} {...extra} />
        {showErr ? <p data-testid={`patient-${key}-error`} className="text-xs text-red-600 mt-1.5">{error}</p> : null}
      </div>
    );
  };

  return (
    <div className="min-h-[100dvh] bg-stone-50">
      <header className="max-w-5xl mx-auto px-4 sm:px-6 md:px-10 pt-6 sm:pt-8 pb-2 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {logoUrl ? (
            <img src={logoUrl} alt="Logo" data-testid="practice-logo" className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl object-contain bg-white border border-stone-200 shrink-0" />
          ) : (
            <div className="w-10 h-10 rounded-xl bg-emerald-900 text-white grid place-items-center shrink-0">
              <ShieldCheck className="w-5 h-5" />
            </div>
          )}
          <div className="min-w-0">
            <div className="text-[10px] sm:text-xs uppercase tracking-widest text-stone-500 truncate">{practice.name}</div>
            <div className="font-display text-base sm:text-lg font-semibold text-emerald-900">{t.title}</div>
          </div>
        </div>
        {!done && (
          <div className="flex items-center gap-1 rounded-full bg-white border border-stone-200 p-1 shrink-0" data-testid="lang-switcher">
            <Globe className="w-4 h-4 text-stone-500 ml-2" />
            <button data-testid="lang-de" onClick={() => setLang("de")}
              className={`px-3 h-8 text-sm rounded-full transition ${lang === "de" ? "bg-emerald-900 text-white" : "text-stone-600 hover:text-emerald-900"}`}>DE</button>
            <button data-testid="lang-en" onClick={() => setLang("en")}
              className={`px-3 h-8 text-sm rounded-full transition ${lang === "en" ? "bg-emerald-900 text-white" : "text-stone-600 hover:text-emerald-900"}`}>EN</button>
          </div>
        )}
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 md:px-10 py-5 sm:py-8">
        {!done && <Progress steps={STEPS} step={step} t={t} />}
        <div className="bg-white rounded-2xl sm:rounded-3xl border border-stone-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-5 sm:p-8 md:p-12">

          {stepKey === "data" && (
            <div data-testid="step-data">
              <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold text-emerald-900 mb-2">{t.yourData}</h1>
              <p className="text-stone-600 mb-6 sm:mb-8 text-base sm:text-lg">{t.dataIntro}</p>
              <form onSubmit={(e) => { e.preventDefault(); setTouched({ firstname: 1, lastname: 1, email: 1, birthdate: 1, street: 1, zip: 1, city: 1 }); if (canNextData) setStep(1); }}>
                <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
                  {reqField("firstname", t.firstname, "text", { autoComplete: "given-name", name: "given-name" })}
                  {reqField("lastname", t.lastname, "text", { autoComplete: "family-name", name: "family-name" })}
                  {reqField("email", t.email, "email", { autoComplete: "email", name: "email", inputMode: "email" },
                    form.email.trim() && !emailValid ? t.emailInvalid : null)}
                  {reqField("birthdate", t.dob, "date", { autoComplete: "bday", name: "bday" })}
                  <div className="sm:col-span-2">{reqField("street", t.street, "text", { autoComplete: "street-address", name: "street-address" })}</div>
                  {reqField("zip", t.zip, "text", { autoComplete: "postal-code", name: "postal-code", inputMode: "numeric", maxLength: 5 },
                    form.zip.trim() && !zipValid ? t.zipInvalid : null)}
                  {reqField("city", t.city, "text", { autoComplete: "address-level2", name: "address-level2" })}
                </div>
                <div className="mt-8 sm:mt-10 flex">
                  <Button type="submit" data-testid="step-data-next" disabled={!canNextData}
                    className="h-14 sm:h-16 px-5 sm:px-8 text-base sm:text-lg rounded-xl bg-emerald-900 hover:bg-emerald-800 text-white disabled:opacity-40 w-full sm:w-auto sm:ml-auto">
                    <span className="sm:hidden truncate">{t.nextToPrivacyShort}</span>
                    <span className="hidden sm:inline">{t.nextToPrivacy}</span>
                    <ArrowRight className="ml-2 w-5 h-5 shrink-0" />
                  </Button>
                </div>
              </form>
            </div>
          )}

          {stepKey === "privacy-read" && (
            <ReadStep testid="step-privacy-read"
              heading={t.privacy} version={policy.version} text={policy.text} loading={loadingDoc}
              consent={privacyConsent} setConsent={setPrivacyConsent}
              questions={policy.questions} answers={privacyAnswers} setAnswer={setAnswer}
              onBack={() => setStep(0)} onNext={() => setStep(2)}
              nextLabel={t.nextToSign} nextShort={t.nextToSignShort} t={t} lang={lang}
              displaySettings={practice?.display_settings} />
          )}

          {stepKey === "privacy-sign" && (
            <SignStep testid="step-privacy-sign"
              heading={t.signHeading(t.privacy)}
              sigRef={privacySigRef} setSigEmpty={setPrivacySigEmpty} sigEmpty={privacySigEmpty}
              onBack={() => setStep(1)} onNext={afterPrivacySign}
              nextLabel={hasContract ? t.nextToContract : t.submit}
              nextShort={hasContract ? t.nextToContractShort : t.submit}
              submitting={!hasContract && submitting} t={t} />
          )}

          {stepKey === "contract-read" && (
            <ReadStep testid="step-contract-read"
              heading={t.contract} version={contract.version} text={contract.text} loading={loadingDoc}
              consent={contractConsent} setConsent={setContractConsent}
              onBack={() => setStep(2)} onNext={() => setStep(4)}
              nextLabel={t.nextToSign} nextShort={t.nextToSignShort} t={t} lang={lang}
              displaySettings={practice?.display_settings} />
          )}

          {stepKey === "contract-sign" && (
            <SignStep testid="step-contract-sign"
              heading={t.signHeading(t.contract)}
              sigRef={contractSigRef} setSigEmpty={setContractSigEmpty} sigEmpty={contractSigEmpty}
              onBack={() => setStep(3)} onNext={submitAll}
              nextLabel={t.submit} nextShort={t.submit} submitting={submitting} t={t} />
          )}

          {done && (
            <div className="text-center py-8 sm:py-12" data-testid="step-done">
              {logoUrl ? (
                <img src={logoUrl} alt={practice.name} data-testid="done-logo"
                  className="w-24 h-24 sm:w-28 sm:h-28 mx-auto rounded-2xl object-contain bg-stone-50 border border-stone-200 mb-5 sm:mb-6" />
              ) : (
                <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto rounded-2xl bg-emerald-900 text-white grid place-items-center mb-5 sm:mb-6">
                  <CheckCircle2 className="w-10 h-10 sm:w-12 sm:h-12" />
                </div>
              )}
              <h1 className="font-display text-2xl sm:text-3xl md:text-5xl font-semibold text-emerald-900 mb-3 sm:mb-4 break-words">
                {hasContract ? t.thanksTwo : t.thanksOne}
              </h1>
              <p className="text-stone-600 text-base sm:text-lg max-w-xl mx-auto px-2">
                {hasContract ? t.storedTwo : t.storedOne}
              </p>
              <p data-testid="done-countdown" className="text-sm text-stone-400 mt-6 sm:mt-8">
                {t.closing(countdown)}
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
